import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  TrendUp, Users, Megaphone, Article, Image as ImageIcon, Star, Lightning, ArrowUpRight
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const stats = [
  { key: "leads", label: "Leads (all-time)", icon: Users, accent: "text-navy", to: "/admin/leads" },
  { key: "reviews", label: "Customer Reviews", icon: Star, accent: "text-navy", to: "/admin/reviews" },
  { key: "blogs", label: "SEO Articles", icon: Article, accent: "text-slate-700", to: "/admin/journal" },
  { key: "creatives", label: "AI Creatives", icon: ImageIcon, accent: "text-slate-700", to: "/admin/creatives" },
  { key: "posts", label: "Scheduled Posts", icon: Megaphone, accent: "text-slate-700", to: "/admin/calendar" },
  { key: "ads", label: "Ad Campaigns", icon: Lightning, accent: "text-slate-700", to: "/admin/ads" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  const load = useCallback(
    () => api.get("/analytics")
      .then((r) => setData(r.data))
      .catch((err) => console.warn("[dashboard] analytics load failed:", err?.message || err)),
    [],
  );
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        eyebrow="Admin / Overview"
        title="Run your marketing like a pro."
        description="One control room for SEO, ads, social content drafts, reviews and leads — built for True North Maintenance."
        action={
          <Link to="/admin/leads">
            <Button data-testid="cta-view-leads" className="pill-navy">View leads <ArrowUpRight className="ml-2" size={14} weight="bold" /></Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        {stats.map((s) => {
          const Icon = s.icon;
          const value = data?.totals?.[s.key] ?? 0;
          return (
            <Link to={s.to} key={s.key}>
              <Card data-testid={`stat-${s.key}`} className="sharp-card p-4 rounded-md shadow-none hover:border-slate-900 transition-colors">
                <div className="flex items-center justify-between">
                  <Icon size={22} weight="duotone" className={s.accent} />
                  <TrendUp size={14} className="text-emerald-500" />
                </div>
                <div className="mt-3 font-display text-3xl font-black text-slate-900">{value}</div>
                <div className="eyebrow mt-1 !tracking-[0.18em]">{s.label}</div>
              </Card>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { to: "/admin/leads", title: "Check new leads", desc: "Customers waiting for a quote come through here first." },
          { to: "/admin/journal", title: "Draft a blog post", desc: "Generate SEO-friendly articles that rank for local keywords." },
          { to: "/admin/creatives", title: "Design a creative", desc: "AI-generated images for your next social post or ad." },
        ].map((c) => (
          <Link key={c.to} to={c.to} className="block group">
            <Card data-testid={`quick-${c.to.slice(1).replace(/\//g, "-")}`} className="sharp-card p-6 hover:border-slate-900 transition-colors">
              <h4 className="font-display font-bold text-lg text-slate-900">{c.title}</h4>
              <p className="text-sm text-slate-600 mt-1">{c.desc}</p>
              <div className="mt-4 text-navy text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                Open <ArrowUpRight size={14} />
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
